// src/components/BooksTable.js
import React, { useState, useEffect } from 'react';
import { WEB_APP_URL, SECRET_TOKEN } from '../config';
import LoadingOverlay from './LoadingOverlay';
import EditBookModal from './EditBookModal';
import book from '../assets/book.png';

// Cache for cover URLs to avoid repeated API calls
const coverCache = {};

const fetchCoverForTitle = async (title) => {
  if (coverCache[title] !== undefined) return coverCache[title];
  
  // Strip "by Author Name" patterns for cleaner searches
  const cleanTitle = title.replace(/\s+by\s+.+$/i, '').trim();
  
  // Try Open Library first
  try {
    const res = await fetch(`https://openlibrary.org/search.json?q=${encodeURIComponent(cleanTitle)}&limit=3`);
    const data = await res.json();
    const withCover = data.docs?.find(doc => doc.cover_i);
    if (withCover) {
      const url = `https://covers.openlibrary.org/b/id/${withCover.cover_i}-M.jpg`;
      coverCache[title] = url;
      return url;
    }
  } catch (err) {
    console.error('Open Library error:', err);
  }

  // Fallback to Google Books
  try {
    const res = await fetch(`https://www.googleapis.com/books/v1/volumes?q=${encodeURIComponent(cleanTitle)}&maxResults=1`);
    const data = await res.json();
    const thumbnail = data.items?.[0]?.volumeInfo?.imageLinks?.thumbnail;
    if (thumbnail) {
      const url = thumbnail.replace('http://', 'https://').replace('zoom=1', 'zoom=2');
      coverCache[title] = url;
      return url;
    }
  } catch (err) {
    console.error('Google Books error:', err);
  }

  coverCache[title] = null;
  return null;
};

// Cache helpers
const getCache = (key) => {
  try {
    const cached = localStorage.getItem(key);
    return cached ? JSON.parse(cached) : null;
  } catch { return null; }
};
const setCache = (key, data) => {
  try { localStorage.setItem(key, JSON.stringify(data)); } catch {}
};

const BooksTable = ({ refreshToggle, onBooksLoaded, onUpdating }) => {
  const [books, setBooks] = useState(() => getCache('books') || []);
  const [covers, setCovers] = useState(() => getCache('covers') || {});
  const [loading, setLoading] = useState(!getCache('books'));
  const [sortBy, setSortBy] = useState('date-desc');
  const [editingBook, setEditingBook] = useState(null);

  useEffect(() => {
    setLoading(true);
    if (onUpdating) onUpdating(true);
    const fetchBooks = async () => {
      try {
        const response = await fetch(`${WEB_APP_URL}?action=getBooks`, {
          redirect: "follow",
          method: "GET"
        });
        const result = await response.json();
        // Sort by date descending (newest first)
        const sorted = (result.books || []).sort((a, b) => 
          new Date(b.date) - new Date(a.date)
        );
        setBooks(sorted);
        setCache('books', sorted);
        if (onBooksLoaded) onBooksLoaded(sorted);
        if (onUpdating) onUpdating(false);
        
        // Fetch covers only for books that don't already have one cached
        const cachedCovers = getCache('covers') || {};
        const booksNeedingCovers = sorted.filter(book => 
          !book.coverUrl && !cachedCovers[book.title]
        );
        
        booksNeedingCovers.forEach((book, index) => {
          setTimeout(async () => {
            const coverUrl = await fetchCoverForTitle(book.title);
            if (coverUrl) {
              setCovers(prev => {
                const updated = { ...prev, [book.title]: coverUrl };
                setCache('covers', updated);
                return updated;
              });
            }
          }, index * 150); // Stagger requests by 150ms
        });
      } catch (error) {
        console.error('Error fetching books:', error);
        if (onUpdating) onUpdating(false);
      }
      setLoading(false);
    };

    fetchBooks();
  }, [refreshToggle]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const renderStars = (rating) => {
    const stars = parseInt(rating, 10) || 0;
    return (
      <div className="book-rating">
        {Array.from({ length: 5 }, (_, i) => (
          <span key={i} className={i < stars ? 'star filled' : 'star'}>
            {i < stars ? '★' : '☆'}
          </span>
        ))}
      </div>
    );
  };

  const handleCoverClick = async (bookTitle) => {
    const url = window.prompt('Enter cover image URL (or leave empty to clear):');
    if (url === null) return; // Cancelled
    
    try {
      const response = await fetch(WEB_APP_URL, {
        method: 'POST',
        body: JSON.stringify({
          token: SECRET_TOKEN,
          action: 'updateBookCover',
          title: bookTitle,
          coverUrl: url
        }),
        redirect: 'follow',
        mode: 'no-cors'
      });
      // With no-cors we can't read response, but assume success
      // Update local state and cache
      setCovers(prev => {
        const updated = { ...prev, [bookTitle]: url };
        setCache('covers', updated);
        return updated;
      });
      // Also update books state if it has coverUrl
      setBooks(prev => prev.map(b => 
        b.title === bookTitle ? { ...b, coverUrl: url } : b
      ));
    } catch (err) {
      console.error('Failed to update cover:', err);
      alert('Failed to update cover. Try again.');
    }
  };

  const handleEditSave = async (newData) => {
    const originalTitle = editingBook.title;
    try {
      await fetch(WEB_APP_URL, {
        method: 'POST',
        body: JSON.stringify({
          token: SECRET_TOKEN,
          action: 'updateBook',
          originalTitle,
          newData
        }),
        redirect: 'follow',
        mode: 'no-cors'
      });
      // Update local state optimistically
      setBooks(prev => prev.map(b => 
        b.title === originalTitle ? { ...b, ...newData } : b
      ));
      if (newData.coverUrl) {
        setCovers(prev => {
          const updated = { ...prev, [newData.title]: newData.coverUrl };
          setCache('covers', updated);
          return updated;
        });
      }
      setEditingBook(null);
    } catch (err) {
      console.error('Failed to update book:', err);
      alert('Failed to update book. Try again.');
    }
  };

  const hasCachedData = books.length > 0;

  const sortedBooks = [...books].sort((a, b) => {
    switch (sortBy) {
      case 'date-asc':
        return new Date(a.date) - new Date(b.date);
      case 'rating-desc':
        return (parseInt(b.rating, 10) || 0) - (parseInt(a.rating, 10) || 0);
      case 'rating-asc':
        return (parseInt(a.rating, 10) || 0) - (parseInt(b.rating, 10) || 0);
      case 'title':
        return a.title.localeCompare(b.title);
      case 'date-desc':
      default:
        return new Date(b.date) - new Date(a.date);
    }
  });
  
  return (
    <div style={{ position: 'relative' }}>
      {loading && !hasCachedData && <LoadingOverlay image={book} message="Loading books..." />}
      {books.length > 0 && (
        <div className="sort-controls">
          <label>Sort by: </label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="date-desc">Newest first</option>
            <option value="date-asc">Oldest first</option>
            <option value="rating-desc">Highest rated</option>
            <option value="rating-asc">Lowest rated</option>
            <option value="title">Title A-Z</option>
          </select>
        </div>
      )}
      <div className="books-grid" style={{ opacity: 1 }}>
        {sortedBooks.length === 0 && !loading ? (
          <p style={{ textAlign: 'center', color: '#666' }}>No books yet. Start reading!</p>
        ) : (
          sortedBooks.map((bookItem, index) => {
            const coverUrl = covers[bookItem.title] || bookItem.coverUrl;
            const isFiveStar = parseInt(bookItem.rating, 10) === 5;
            const displayTitle = bookItem.title.replace(/\s+by\s+.+$/i, '').trim();
            return (
              <div key={index} className={`book-card${isFiveStar ? ' five-star' : ''}`}>
                <div className="book-cover-wrapper" onClick={() => handleCoverClick(bookItem.title)}>
                  {coverUrl ? (
                    <img 
                      src={coverUrl} 
                      alt={displayTitle}
                      className="book-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="book-spine"></div>
                  )}
                  <div className="cover-edit-overlay">📷</div>
                </div>
                <div className="book-content">
                  <h4 className="book-title">{displayTitle}</h4>
                  {renderStars(bookItem.rating)}
                  {bookItem.comments && (
                    <p className="book-comments">{bookItem.comments}</p>
                  )}
                  <span className="book-date">{formatDate(bookItem.date)}</span>
                  <button 
                    className="edit-book-btn"
                    onClick={(e) => { e.stopPropagation(); setEditingBook(bookItem); }}
                  >✏️</button>
                </div>
              </div>
            );
          })
        )}
      </div>
      {editingBook && (
        <EditBookModal
          book={editingBook}
          onSave={handleEditSave}
          onClose={() => setEditingBook(null)}
        />
      )}
    </div>
  );
};

export default BooksTable;
